import { IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';
import {
  ArrivalSummary,
  CustomerForecastSummary,
  InventorySummary,
  ISummaryData,
  OrderSummary,
  SalesResultSummary,
} from '../domains/entities/SummaryData';
import { IInventoryOrder } from '../domains/entities/InventoryOrder';
import { IBeforeAfterValue } from '../domains/entities/MonthlyData';

/**
 * 数値集計行の計算を提供するComposable
 */
export const useTransactionSummaryCalclation = () => {
  /**
   * 数値集計データから指定されたトランザクションキーと月に基づいてデータを取得する関数
   * @param data - 数値集計データ
   * @param transactionKey - 対象のトランザクションキー
   * @param month - 対象の月 (相対月)
   * @param isOrderMode - 発注モードかどうか
   * @param orderEditData - 発注編集データ（発注モード時のみ）
   */
  const getTransactionData = (
    data: ISummaryData,
    transactionKey: string,
    month: number,
    isOrderMode = false,
    orderEditData: IInventoryOrder = {}
  ): number => {
    if (!data || !transactionKey) {
      return 0;
    }
    const targetYearMonth = DateService.getShiftedIYearMonthFromNowDateByMonths(month);
    switch (transactionKey) {
      case 'arrival':
        return _getArrivalSummary(targetYearMonth, data.arrivalSummary);

      case 'alreadyArrival':
        return _getDeliveredArrivalSummary(targetYearMonth, data.orderSummary);

      case 'expectedArrival':
        return isOrderMode
          ? _getPreArrivalSummary(targetYearMonth, data.orderSummary)
          : _getExpectedArrivalSummary(targetYearMonth, data.orderSummary);

      case 'announce':
        return _getAnnounceSummary(targetYearMonth, data.customerForecastSummary);

      case 'endInventory':
        return _getEndInventorySummary(targetYearMonth, data.inventorySummary);

      case 'nowInventory':
        return _getNowInventorySummary(targetYearMonth, data.inventorySummary);

      case 'expectedEndInventory':
        return _getExpectedInventorySummary(targetYearMonth, month, data.inventorySummary, data.orderSummary, data.customerForecastSummary);

      case 'salesResult':
        return _getSalesResultSummary(targetYearMonth, data.salesResultSummary);

      case 'turnOver':
        return _getTurnOverSummary(targetYearMonth, month, data.inventorySummary, data.orderSummary, data.customerForecastSummary);

      case 'amount':
        return _getOrderAmountSummary(month, orderEditData);

      default:
        return 0;
    }
  };

  /**
   * 発注数量集計数値を取得する
   * @param month - 対象の月 (相対月)
   * @param orderEditData - 発注編集データ
   */
  const _getOrderAmountSummary = (month: number, orderEditData: IInventoryOrder): number => {
    if (!orderEditData || Object.keys(orderEditData).length === 0) {
      return 0;
    }
    let total = 0;
    for (const businessId of Object.keys(orderEditData)) {
      const monthData = orderEditData[businessId]?.[month];
      if (monthData?.amount) {
        total += monthData.amount;
      }
    }
    return total;
  };

  /**
   * 入荷集計数値を取得する
   * @param targetYearMonth - 対象の年と月
   * @param data - 入荷集計データ
   */
  const _getArrivalSummary = (targetYearMonth: IYearMonth, data: ArrivalSummary[]): number => {
    if (data.length === 0) {
      return 0;
    }
    const targetMonthArrival = data.filter(
      (item) => item.targetMonth === targetYearMonth.month && item.targetYear === targetYearMonth.year
    );
    if (targetMonthArrival.length > 0) {
      return targetMonthArrival[0].arrivalAmountSummary;
    } else {
      return 0;
    }
  };

  /**
   * 予入荷集計数値を取得する
   * @param targetYearMonth - 対象の年と月
   * @param data - 発注集計データ
   */
  const _getExpectedArrivalSummary = (targetYearMonth: IYearMonth, data: OrderSummary[]): number => {
    if (data.length === 0) {
      return 0;
    }
    const targetMonthOrder = data.filter((item) => item.targetMonth === targetYearMonth.month && item.targetYear === targetYearMonth.year);
    if (targetMonthOrder.length > 0) {
      return targetMonthOrder[0].preOrderAmountSummary + targetMonthOrder[0].deliveredOrderAmountSummary;
    } else {
      return 0;
    }
  };

  /**
   * 入荷済集計数値を取得する
   * @param targetYearMonth - 対象の年と月
   * @param data - 発注集計データ
   */
  const _getDeliveredArrivalSummary = (targetYearMonth: IYearMonth, data: OrderSummary[]): number => {
    if (data.length === 0) {
      return 0;
    }
    const targetMonthOrder = data.filter((item) => item.targetMonth === targetYearMonth.month && item.targetYear === targetYearMonth.year);
    if (targetMonthOrder.length > 0) {
      return targetMonthOrder[0].deliveredOrderAmountSummary;
    } else {
      return 0;
    }
  };

  /**
   * 予入荷集計数値を取得する
   * @param targetYearMonth - 対象の年と月
   * @param data - 発注集計データ
   */
  const _getPreArrivalSummary = (targetYearMonth: IYearMonth, data: OrderSummary[]): number => {
    if (data.length === 0) {
      return 0;
    }
    const targetMonthOrder = data.filter((item) => item.targetMonth === targetYearMonth.month && item.targetYear === targetYearMonth.year);
    if (targetMonthOrder.length > 0) {
      return targetMonthOrder[0].preOrderAmountSummary;
    } else {
      return 0;
    }
  };

  /**
   * 客先内示集計数値を取得する
   * @param targetYearMonth - 対象の年と月
   * @param data - 客先内示集計データ
   */
  const _getAnnounceSummary = (targetYearMonth: IYearMonth, data: CustomerForecastSummary[]): number => {
    if (data.length === 0) {
      return 0;
    }
    const targetMonthCustomerForecast = data
      .filter((item) => item.targetMonth === targetYearMonth.month && item.targetYear === targetYearMonth.year)
      .sort((a, b) => b.receivedYear * 100 + b.receivedMonth - (a.receivedYear * 100 + a.receivedMonth));
    if (targetMonthCustomerForecast.length > 0) {
      return targetMonthCustomerForecast[0].customerForecastAmountSummary;
    } else {
      return 0;
    }
  };

  /**
   * 末在庫集計数値を取得する
   * @param targetYearMonth - 対象の年と月
   * @param data - 在庫集計データ
   */
  const _getEndInventorySummary = (targetYearMonth: IYearMonth, data: InventorySummary[]): number => {
    if (data.length === 0) {
      return 0;
    }
    const targetMonthInventory = data.filter(
      (item) => item.targetMonth === targetYearMonth.month && item.targetYear === targetYearMonth.year
    );
    if (targetMonthInventory.length > 0) {
      return targetMonthInventory[0].inventoryAmountSummary;
    } else {
      return 0;
    }
  };

  /**
   * 現在庫集計数値を取得する
   * @param targetYearMonth - 対象の年と月
   * @param data - 在庫集計データ
   */
  const _getNowInventorySummary = (targetYearMonth: IYearMonth, data: InventorySummary[]): number => {
    if (data.length === 0) {
      return 0;
    }
    const targetMonthInventory = data.sort((a, b) => b.targetYear * 100 + b.targetMonth - (a.targetYear * 100 + a.targetMonth));
    if (targetMonthInventory.length > 0) {
      return targetMonthInventory[0].inventoryAmountSummary;
    } else {
      return 0;
    }
  };

  /**
   * 予在庫集計数値を取得する
   * @param targetYearMonth - 対象の年と月
   * @param month - 対象の月 (相対月)
   * @param dataInventory - 在庫集計データ
   * @param dataOrder - 発注集計データ
   * @param dataCustomerForecast - 客先内示集計データ
   */
  const _getExpectedInventorySummary = (
    targetYearMonth: IYearMonth,
    month: number,
    dataInventory: InventorySummary[],
    dataOrder: OrderSummary[],
    dataCustomerForecast: CustomerForecastSummary[]
  ): number => {
    const targetBeforeMonth = DateService.getShiftedIYearMonthByMonths(targetYearMonth.year, targetYearMonth.month, -1);
    const targetBeforeMonthInventory =
      month <= 0
        ? _getEndInventorySummary(targetBeforeMonth, dataInventory)
        : _getExpectedInventorySummary(targetBeforeMonth, month - 1, dataInventory, dataOrder, dataCustomerForecast);
    const targetMonthExpectedArrival = _getExpectedArrivalSummary(targetYearMonth, dataOrder);
    const targetMonthCustomerForecast = _getAnnounceSummary(targetYearMonth, dataCustomerForecast);
    // 予末在庫＝前月末在庫＋当月予入荷-当月内示
    return targetBeforeMonthInventory + targetMonthExpectedArrival - targetMonthCustomerForecast;
  };

  /**
   * 販売実績集計数値を取得する
   * @param targetYearMonth - 対象の年と月
   * @param data - 販売実績集計データ
   */
  const _getSalesResultSummary = (targetYearMonth: IYearMonth, data: SalesResultSummary[]): number => {
    if (data.length === 0) {
      return 0;
    }
    const targetSalesResultMonth = data.filter(
      (item) => item.targetMonth === targetYearMonth.month && item.targetYear === targetYearMonth.year
    );
    if (targetSalesResultMonth.length > 0) {
      return targetSalesResultMonth[0].salesResultAmountSummary;
    } else {
      return 0;
    }
  };

  /**
   * 在庫回転率集計数値を取得する
   * @param targetYearMonth - 対象の年と月
   * @param month - 対象の月 (相対月)
   * @param dataInventory - 在庫集計データ
   * @param dataOrder - 発注集計データ
   * @param dataCustomerForecast - 客先内示集計データ
   */
  const _getTurnOverSummary = (
    targetYearMonth: IYearMonth,
    month: number,
    dataInventory: InventorySummary[],
    dataOrder: OrderSummary[],
    dataCustomerForecast: CustomerForecastSummary[]
  ): number => {
    const targetMonthInventory = _getExpectedInventorySummary(targetYearMonth, month, dataInventory, dataOrder, dataCustomerForecast);
    const targetMonthAnounce = _getAnnounceSummary(targetYearMonth, dataCustomerForecast);
    if (targetMonthInventory === 0 || targetMonthAnounce === 0) {
      return 0;
    }
    // 回転率＝当月予末在庫 / 当月内示
    return targetMonthInventory / targetMonthAnounce;
  };

  /**
   * 発注数量を含めた予在庫集計数値を取得する（発注後の値）
   * @param targetYearMonth - 対象の年と月
   * @param month - 対象の月 (相対月)
   * @param dataInventory - 在庫集計データ
   * @param dataOrder - 発注集計データ
   * @param dataCustomerForecast - 客先内示集計データ
   * @param orderEditData - 発注編集データ
   */
  const _getExpectedInventorySummaryWithOrder = (
    targetYearMonth: IYearMonth,
    month: number,
    dataInventory: InventorySummary[],
    dataOrder: OrderSummary[],
    dataCustomerForecast: CustomerForecastSummary[],
    orderEditData: IInventoryOrder
  ): number => {
    const targetBeforeMonth = DateService.getShiftedIYearMonthByMonths(targetYearMonth.year, targetYearMonth.month, -1);
    const targetBeforeMonthInventory =
      month <= 0
        ? _getEndInventorySummary(targetBeforeMonth, dataInventory)
        : _getExpectedInventorySummaryWithOrder(
            targetBeforeMonth,
            month - 1,
            dataInventory,
            dataOrder,
            dataCustomerForecast,
            orderEditData
          );
    const targetMonthExpectedArrival = _getExpectedArrivalSummary(targetYearMonth, dataOrder);
    const targetMonthCustomerForecast = _getAnnounceSummary(targetYearMonth, dataCustomerForecast);
    const targetMonthOrderAmount = _getOrderAmountSummary(month, orderEditData);
    // 予末在庫＝前月末在庫＋当月予入荷＋当月発注数量-当月内示
    return targetBeforeMonthInventory + targetMonthExpectedArrival + targetMonthOrderAmount - targetMonthCustomerForecast;
  };

  /**
   * 発注数量を含めた在庫回転率集計数値を取得する（発注後の値）
   * @param targetYearMonth - 対象の年と月
   * @param month - 対象の月 (相対月)
   * @param dataInventory - 在庫集計データ
   * @param dataOrder - 発注集計データ
   * @param dataCustomerForecast - 客先内示集計データ
   * @param orderEditData - 発注編集データ
   */
  const _getTurnOverSummaryWithOrder = (
    targetYearMonth: IYearMonth,
    month: number,
    dataInventory: InventorySummary[],
    dataOrder: OrderSummary[],
    dataCustomerForecast: CustomerForecastSummary[],
    orderEditData: IInventoryOrder
  ): number => {
    const targetMonthInventory = _getExpectedInventorySummaryWithOrder(
      targetYearMonth,
      month,
      dataInventory,
      dataOrder,
      dataCustomerForecast,
      orderEditData
    );
    const targetMonthAnounce = _getAnnounceSummary(targetYearMonth, dataCustomerForecast);
    if (targetMonthInventory === 0 || targetMonthAnounce === 0) {
      return 0;
    }
    // 回転率＝当月予末在庫 / 当月内示
    return targetMonthInventory / targetMonthAnounce;
  };

  /**
   * 発注前後の予在庫集計数値を取得する
   * @param data - 数値集計データ
   * @param month - 対象の月 (相対月)
   * @param orderEditData - 発注編集データ
   */
  const getExpectedEndInventoryBeforeAfter = (data: ISummaryData, month: number, orderEditData: IInventoryOrder): IBeforeAfterValue => {
    if (!data) {
      return { before: 0, after: 0, hasChange: false };
    }
    const targetYearMonth = DateService.getShiftedIYearMonthFromNowDateByMonths(month);
    const before = _getExpectedInventorySummary(
      targetYearMonth,
      month,
      data.inventorySummary,
      data.orderSummary,
      data.customerForecastSummary
    );
    const after = _getExpectedInventorySummaryWithOrder(
      targetYearMonth,
      month,
      data.inventorySummary,
      data.orderSummary,
      data.customerForecastSummary,
      orderEditData
    );
    return { before, after, hasChange: before !== after };
  };

  /**
   * 発注前後の在庫回転率集計数値を取得する
   * @param data - 数値集計データ
   * @param month - 対象の月 (相対月)
   * @param orderEditData - 発注編集データ
   */
  const getTurnOverBeforeAfter = (data: ISummaryData, month: number, orderEditData: IInventoryOrder): IBeforeAfterValue => {
    if (!data) {
      return { before: 0, after: 0, hasChange: false };
    }
    const targetYearMonth = DateService.getShiftedIYearMonthFromNowDateByMonths(month);
    const before = _getTurnOverSummary(targetYearMonth, month, data.inventorySummary, data.orderSummary, data.customerForecastSummary);
    const after = _getTurnOverSummaryWithOrder(
      targetYearMonth,
      month,
      data.inventorySummary,
      data.orderSummary,
      data.customerForecastSummary,
      orderEditData
    );
    return { before, after, hasChange: before !== after };
  };

  return {
    getTransactionData,
    getExpectedEndInventoryBeforeAfter,
    getTurnOverBeforeAfter,
  };
};
